import { type FC, useEffect, useRef, useState } from 'react';

import ArrowIosDown from '@crehana/compass.icons.outline/ArrowIosDown';
import ArrowIosLeft from '@crehana/compass.icons.outline/ArrowIosLeft';
import ArrowIosRight from '@crehana/compass.icons.outline/ArrowIosRight';
import Filter from '@crehana/compass.icons.outline/Filter';
import { Checkbox } from '@crehana/compass.ui';
import { clsx } from 'clsx';

import { BASE_OPTION_STYLES } from './constants';
import type { TNestedSelectProps, TOption } from './types';

const SelectNested: FC<React.PropsWithChildren<TNestedSelectProps>> = ({
  backLabel,
  hint,
  options,
  disabled,
  optionsSelected,
}) => {
  const container = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [currentOption, setCurrentOption] = useState<TOption | null>(null);
  const [selectOptions, setSelectOptions] = useState(options);
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      // biome-ignore lint/style/useBlockStatements: single-line early return
      if (!e.target) return;

      const target = e.target as Element;

      if (container.current && !container.current?.contains(target) && isOpen) {
        setIsOpen(false);
      }
    };

    const onKeydown = (e: KeyboardEvent) => {
      // biome-ignore lint/style/useBlockStatements: single-line early return
      if (e.keyCode === 27) setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('keydown', onKeydown);
      document.addEventListener('click', onClickOutside, true);
    } else {
      document.removeEventListener('click', onClickOutside, true);
      document.removeEventListener('keydown', onKeydown);
      setCurrentOption(null);
      setCurrentIndex(null);
    }

    return () => {
      document.removeEventListener('click', onClickOutside, true);
      document.removeEventListener('keydown', onKeydown);
    };
  }, [isOpen]);

  useEffect(() => {
    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (options.length) setSelectOptions(options);
  }, [options]);

  const handleOption = (idx: number) => {
    const nextOptions = [...selectOptions];

    if (currentIndex !== null) {
      nextOptions[currentIndex].subItems[idx].isChecked =
        !nextOptions[currentIndex].subItems[idx].isChecked;
      setSelectOptions(nextOptions);
      // biome-ignore lint/style/useBlockStatements: single-line early return
      if (optionsSelected) optionsSelected(nextOptions);
    }
  };

  return (
    <div
      className={clsx(
        // Select Wrapper
        'tw-ml-0.8',
        'tw-rounded-1',
        'tw-min-w-min tw-h-full',
        'tw-flex tw-justify-end',
      )}
      ref={container}
    >
      <button
        disabled={disabled}
        className={clsx(
          'tw-h-auto',
          'tw-flex tw-items-center tw-justify-between',
          'tw-px-1.2 tw-py-0.8',
          'tw-font-link1',
          'tw-rounded-1 tw-border tw-border-solid tw-border-neutral-light-500 dark:tw-border-none',
          'tw-bg-neutral-light-100 dark:tw-bg-neutral-dark-700',
          'hover:tw-text-neutral-dark-600 dark:hover:tw-text-neutral-light-400 tw-transition-all',
          isOpen &&
            'tw-border-primary-light-500 tw-text-primary-light-500 dark:tw-text-secondary-light-400 hover:tw-border-primary-light-400 hover:tw-text-primary-light-400 hover:dark:tw-text-secondary-light-400',
          disabled &&
            'tw-bg-neutral-light-400 tw-text-neutral-light-600 tw-border-none',
          !disabled &&
            'tw-text-neutral-light-800 dark:tw-text-neutral-light-100',
        )}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Filter size={16} className="tw-fill-current" />
        <span className="tw-mx-0.8">{hint}</span>
        <ArrowIosDown
          size={16}
          className={clsx(
            'tw-fill-current tw-transition-transform',
            isOpen ? 'tw-rotate-180' : 'tw-rotate-0',
          )}
        />
      </button>
      {isOpen && (
        <div
          className={clsx(
            // Options Container Wrapper
            'tw-rounded-1',
            'tw-z-dialog tw-absolute tw-overflow-auto',
            'tw-top-[100%] tw-right-0',
            'tw-w-28 tw-max-h-24',
            'tw-shadow-03 dark:tw-shadow-none tw-bg-neutral-light-100 dark:tw-bg-neutral-dark-700',
          )}
        >
          {currentOption ? (
            <>
              <button
                type="button"
                className={clsx(
                  // Back Button
                  'tw-flex tw-items-center',
                  'tw-py-0 tw-px-2.4',
                  'tw-w-full tw-h-4.8',
                  'tw-font-caption',
                  'tw-transition-all',
                  'tw-text-primary-light-500 dark:tw-text-neutral-light-100 hover:tw-text-secondary-light-400 hover:dark:tw-text-secondary-light-400',
                )}
                onClick={() => setCurrentOption(null)}
              >
                <ArrowIosLeft size={20} className="tw-fill-current" />
                {backLabel}
              </button>
              {currentOption.subItems.map((subItem, idx) => (
                <button
                  type="button"
                  key={subItem.value}
                  className={clsx('tw-justify-start', BASE_OPTION_STYLES)}
                  onClick={e => {
                    e.preventDefault();
                    handleOption(idx);
                  }}
                >
                  <Checkbox
                    size="s"
                    checked={!!subItem.isChecked}
                    onChange={e => {
                      e.preventDefault();
                      handleOption(idx);
                    }}
                    className="mr-16"
                  />
                  <p className="tw-line-clamp-2 tw-text-left">
                    {subItem.label}
                  </p>
                </button>
              ))}
            </>
          ) : (
            selectOptions.map((option, idx) => (
              // Options styles
              <button
                type="button"
                key={option.value}
                className={clsx('tw-justify-between', BASE_OPTION_STYLES)}
                onClick={() => {
                  setCurrentOption(option);
                  setCurrentIndex(idx);
                }}
              >
                <p>{option.label}</p>
                <ArrowIosRight
                  size={20}
                  className="tw-text-primary-light-500 dark:tw-text-secondary-light-500 tw-fill-current"
                />
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default SelectNested;
